import { useCallback, useRef, useState } from "react";
import { FileUp, FileText, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const ALLOWED = ["pdf", "docx", "png", "jpg", "jpeg"];

export const FileDropzone = ({ file, onFileChange }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");

  const acceptFile = useCallback(
    (candidate) => {
      if (!candidate) return;
      const ext = candidate.name.split(".").pop()?.toLowerCase();
      if (!ALLOWED.includes(ext)) {
        setError("Allowed file types are PDF, DOCX, PNG, JPG, and JPEG.");
        return;
      }
      if (candidate.size > 10 * 1024 * 1024) {
        setError("File exceeds the 10 MB limit.");
        return;
      }
      setError("");
      onFileChange(candidate);
    },
    [onFileChange]
  );

  return (
    <div>
      <div
        data-testid="order-file-dropzone"
        role="button"
        tabIndex={0}
        aria-label="Upload supporting document"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          acceptFile(e.dataTransfer.files?.[0]);
        }}
        className={`relative rounded-xl border border-dashed p-5 transition-colors cursor-pointer ${
          dragOver ? "border-primary bg-primary/10" : "border-border bg-secondary/40 hover:bg-secondary/60"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          data-testid="order-file-input"
          className="hidden"
          accept=".pdf,.docx,.png,.jpg,.jpeg"
          onChange={(e) => acceptFile(e.target.files?.[0])}
        />
        {file ? (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/15 border border-primary/30">
                <FileText className="h-5 w-5 text-primary" />
              </span>
              <div className="min-w-0">
                <p data-testid="order-file-name" className="text-sm font-medium truncate">{file.name}</p>
                <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              data-testid="order-file-remove-button"
              aria-label="Remove file"
              onClick={(e) => {
                e.stopPropagation();
                onFileChange(null);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <FileUp className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Drop your supporting document here, or click to browse</p>
            <p className="text-xs text-muted-foreground">PDF, DOCX, PNG, JPG, JPEG &middot; up to 10 MB</p>
          </div>
        )}
      </div>
      {error && (
        <p data-testid="order-file-error" className="mt-2 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
